'use client';

import React from 'react';
import { type ReactNode, type Ref, createContext, useContext } from 'react';
import { ChevronDownIcon, ChevronUpIcon, MinusIcon, PlusIcon } from '@milanote/roma-icons';
import type {
    InputProps as AriaInputProps,
    NumberFieldProps as AriaNumberFieldProps,
    PressEvent,
} from 'react-aria-components';
import {
    Button as AriaButton,
    ButtonContext,
    Group as AriaGroup,
    Input as AriaInput,
    NumberField as AriaNumberField,
    NumberFieldStateContext,
    useSlottedContext,
} from 'react-aria-components';
import { Field } from '../field';
import { cx, sortCx } from '../../../utils/cx';

const SEMANTIC_NAME_BASE = 'RomaInputNumberBase';
const SEMANTIC_NAME_FIELD = 'RomaNumberField';
const SEMANTIC_NAME = 'RomaInputNumber';

const NumberFieldContext = createContext<InputNumberContextProps>({});

type InputNumberContextProps = Pick<
    InputNumberBaseProps,
    'size' | 'isDisabled' | 'isInvalid' | 'wrapperClassName' | 'inputClassName' | 'orientation' | 'canDecrementToEmpty'
> &
    Pick<AriaNumberFieldProps, 'isReadOnly'>;

export type InputNumberOrientation = 'vertical' | 'horizontal';

export interface InputNumberBaseProps extends Omit<AriaInputProps, 'size'> {
    /**
     * Input size.
     * @default "sm"
     */
    size?: 'sm' | 'md' | 'lg';
    /** Placeholder text. */
    placeholder?: string;
    /** Class name for the input. */
    inputClassName?: string;
    /** Class name for the input wrapper. */
    wrapperClassName?: string;
    /** Ref for the wrapping group element. */
    groupRef?: Ref<HTMLDivElement>;
    /** Whether the field is invalid. */
    isInvalid?: boolean;
    /** Whether the field is disabled. */
    isDisabled?: boolean;
    /** Whether the field is required. */
    isRequired?: boolean;
    /** The stepper button layout. */
    orientation?: InputNumberOrientation;
    /** Whether decrementing from the minimum value clears the field. */
    canDecrementToEmpty?: boolean;
}

const styles = sortCx({
    stepperButton: [
        'flex flex-1 cursor-pointer items-center justify-center text-fg-quaternary outline-hidden transition duration-100 ease-linear',
        'hover:bg-primary_hover hover:text-fg-quaternary_hover',
        'disabled:cursor-default disabled:bg-disabled_subtle disabled:text-fg-disabled',
    ].join(' '),
    horizontalIcon: 'size-3',
    verticalIncrementButton: '-mt-px',
    verticalDecrementButton:
        '-mb-px border-t border-primary group-disabled:border-disabled group-invalid:border-error_subtle group-invalid:focus-within:border-error',
    sizes: {
        sm: {
            wrapper: 'rounded-xs',
            input: 'rounded-xs px-3 py-1.5 text-sm',
            inputWithVerticalButtons: 'pr-9',
            inputWithHorizontalButtons: 'min-w-0 flex-1 px-3 text-left',
            verticalButtons: 'w-7 rounded-r-xs',
            verticalIcon: 'size-3 stroke-3',
            horizontalButton: 'w-8 shrink-0 flex-none',
            horizontalDecrementButton: 'border-y border-l',
            horizontalIncrementButton: 'rounded-r-[calc(var(--radius-xs)-1px)] border-y border-r border-l',
        },
        md: {
            wrapper: 'rounded-xs',
            input: 'rounded-xs px-3.5 py-2.5 text-md',
            inputWithVerticalButtons: 'pr-9',
            inputWithHorizontalButtons: 'min-w-0 flex-1 px-3 text-left',
            verticalButtons: 'w-7 rounded-r-xs',
            verticalIcon: 'size-3 stroke-3',
            horizontalButton: 'w-10 shrink-0 flex-none',
            horizontalDecrementButton: 'border-y border-l',
            horizontalIncrementButton: 'rounded-r-[calc(var(--radius-xs)-1px)] border-y border-r border-l',
        },
        lg: {
            wrapper: 'rounded-sm',
            input: 'rounded-sm px-3.5 py-2.5 text-lg',
            inputWithVerticalButtons: 'pr-9.5',
            inputWithHorizontalButtons: 'min-w-0 flex-1 px-3 text-left',
            verticalButtons: 'w-7.5 rounded-r-sm',
            verticalIcon: 'size-3.5 stroke-[2.57px]',
            horizontalButton: 'w-11 shrink-0 flex-none',
            horizontalDecrementButton: 'border-y border-l',
            horizontalIncrementButton: 'rounded-r-[calc(var(--radius-sm)-1px)] border-y border-r border-l',
        },
    },
});

type NumberFieldStepperButtonProps = {
    Icon: React.ElementType;
    buttonClassName?: string;
    iconClassName?: string;
};

const NumberFieldIncrementButton: React.FC<NumberFieldStepperButtonProps> = ({
    Icon,
    buttonClassName,
    iconClassName,
}) => {
    return (
        <AriaButton slot="increment" aria-label="Increase value" className={cx(styles.stepperButton, buttonClassName)}>
            <Icon aria-hidden="true" className={iconClassName} />
        </AriaButton>
    );
};

/** Preserves React Aria's decrement button behaviour while allowing its minimum value to be cleared. */
const NumberFieldDecrementButton: React.FC<NumberFieldStepperButtonProps & { canClearOnDecrement: boolean }> = ({
    canClearOnDecrement,
    Icon,
    buttonClassName,
    iconClassName,
}) => {
    const numberFieldState = useContext(NumberFieldStateContext);
    const decrementButtonProps = useSlottedContext(ButtonContext, 'decrement');

    const shouldClearOnDecrement =
        canClearOnDecrement &&
        numberFieldState?.minValue !== undefined &&
        numberFieldState.numberValue === numberFieldState.minValue;

    const isEmptyValue = Number.isNaN(numberFieldState?.numberValue);

    const newDecrementButtonProps = {
        ...decrementButtonProps,
        isDisabled: isEmptyValue || (decrementButtonProps?.isDisabled && !shouldClearOnDecrement),
        onPress: (event: PressEvent) => {
            if (shouldClearOnDecrement) {
                numberFieldState?.setNumberValue(NaN);
                return;
            }

            decrementButtonProps?.onPress?.(event);
        },
    };

    return (
        <ButtonContext.Provider value={{ slots: { decrement: newDecrementButtonProps } }}>
            <AriaButton
                slot="decrement"
                aria-label="Decrease value"
                className={cx(styles.stepperButton, buttonClassName)}
            >
                <Icon aria-hidden="true" className={iconClassName} />
            </AriaButton>
        </ButtonContext.Provider>
    );
};

export const InputNumberBase = React.forwardRef<HTMLInputElement, Omit<InputNumberBaseProps, 'label' | 'hint'>>(
    (
        {
            groupRef,
            size = 'sm',
            isInvalid,
            isDisabled,
            placeholder,
            wrapperClassName,
            inputClassName,
            orientation,
            canDecrementToEmpty,
            // Omit this prop to avoid invalid HTML attribute warning
            isRequired: _isRequired,
            ...inputProps
        },
        ref,
    ) => {
        const context = useContext(NumberFieldContext);

        const inputSize = context?.size || size;
        const inputIsInvalid = isInvalid ?? context?.isInvalid;
        const inputIsDisabled = isDisabled ?? context?.isDisabled;
        const inputIsReadOnly = context?.isReadOnly ?? false;
        const inputOrientation = orientation ?? context?.orientation ?? 'vertical';
        const inputCanDecrementToEmpty = canDecrementToEmpty ?? context?.canDecrementToEmpty ?? false;
        const canClearOnDecrement = inputCanDecrementToEmpty && !inputIsDisabled && !inputIsReadOnly;
        const horizontalButtonBorderClassName = inputIsInvalid
            ? 'border-error'
            : inputIsDisabled
              ? 'border-disabled'
              : 'border-primary';

        const inputBaseClassName = cx(
            'm-0 w-full bg-transparent text-primary outline-hidden placeholder:text-placeholder',
            'autofill:text-primary autofill:bg-transparent',
            inputIsDisabled && 'cursor-default text-disabled text-ellipsis',
            'group-disabled:cursor-default group-disabled:text-disabled group-disabled:text-ellipsis',
            styles.sizes[inputSize].input,
            context?.inputClassName,
            inputClassName,
        );

        return (
            <AriaGroup
                isDisabled={inputIsDisabled}
                isInvalid={inputIsInvalid}
                ref={groupRef}
                className={cx(
                    SEMANTIC_NAME_BASE,
                    'relative flex w-full flex-row items-stretch bg-primary transition duration-100 ease-linear ring-1 ring-primary ring-inset',
                    'after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:ring-inset after:transition after:duration-100 after:ease-linear',
                    styles.sizes[inputSize].wrapper,
                    inputOrientation === 'horizontal' && 'overflow-hidden',
                    'focus-within:after:ring-1 focus-within:after:ring-primary_focused has-[[data-focus-visible]]:after:ring-2',

                    inputIsDisabled && 'cursor-default bg-disabled_subtle ring-disabled',
                    'group-disabled:cursor-default group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

                    inputIsInvalid && 'ring-error_subtle focus-within:after:ring-error',
                    'group-invalid:ring-error_subtle group-invalid:focus-within:after:ring-error',

                    context?.wrapperClassName,
                    wrapperClassName,
                )}
            >
                {inputOrientation === 'horizontal' ? (
                    <>
                        <AriaInput
                            {...inputProps}
                            ref={ref}
                            placeholder={placeholder}
                            className={cx(inputBaseClassName, styles.sizes[inputSize].inputWithHorizontalButtons)}
                        />
                        <NumberFieldDecrementButton
                            Icon={MinusIcon}
                            buttonClassName={cx(
                                styles.sizes[inputSize].horizontalButton,
                                styles.sizes[inputSize].horizontalDecrementButton,
                                horizontalButtonBorderClassName,
                            )}
                            iconClassName={styles.horizontalIcon}
                            canClearOnDecrement={canClearOnDecrement}
                        />
                        <NumberFieldIncrementButton
                            Icon={PlusIcon}
                            buttonClassName={cx(
                                styles.sizes[inputSize].horizontalButton,
                                styles.sizes[inputSize].horizontalIncrementButton,
                                horizontalButtonBorderClassName,
                            )}
                            iconClassName={styles.horizontalIcon}
                        />
                    </>
                ) : (
                    <>
                        <AriaInput
                            {...inputProps}
                            ref={ref}
                            placeholder={placeholder}
                            className={cx(inputBaseClassName, styles.sizes[inputSize].inputWithVerticalButtons)}
                        />
                        <div
                            className={cx(
                                'flex shrink-0 flex-col overflow-hidden border border-primary bg-primary text-fg-quaternary',
                                'group-disabled:border-disabled group-disabled:bg-disabled_subtle group-invalid:border-error_subtle group-invalid:focus-within:border-error',
                                styles.sizes[inputSize].verticalButtons,
                            )}
                        >
                            <NumberFieldIncrementButton
                                Icon={ChevronUpIcon}
                                buttonClassName={styles.verticalIncrementButton}
                                iconClassName={styles.sizes[inputSize].verticalIcon}
                            />
                            <NumberFieldDecrementButton
                                Icon={ChevronDownIcon}
                                buttonClassName={styles.verticalDecrementButton}
                                iconClassName={styles.sizes[inputSize].verticalIcon}
                                canClearOnDecrement={canClearOnDecrement}
                            />
                        </div>
                    </>
                )}
            </AriaGroup>
        );
    },
);

InputNumberBase.displayName = SEMANTIC_NAME_BASE;

export interface NumberFieldProps
    extends AriaNumberFieldProps,
        Pick<
            InputNumberBaseProps,
            'size' | 'wrapperClassName' | 'inputClassName' | 'orientation' | 'canDecrementToEmpty'
        > {}

export const NumberField = ({
    className,
    orientation = 'vertical',
    canDecrementToEmpty,
    ...props
}: NumberFieldProps) => {
    return (
        <NumberFieldContext.Provider value={{ ...props, orientation, canDecrementToEmpty }}>
            <AriaNumberField
                {...props}
                data-input-wrapper
                className={(state) =>
                    cx(
                        SEMANTIC_NAME_FIELD,
                        'group flex h-max w-full flex-col items-start justify-start gap-1.5',
                        typeof className === 'function' ? className(state) : className,
                    )
                }
            />
        </NumberFieldContext.Provider>
    );
};

NumberField.displayName = SEMANTIC_NAME_FIELD;

export interface InputNumberProps extends NumberFieldProps {
    /** Placeholder text. */
    placeholder?: string;
    /** Label text for the input. */
    label?: string;
    /** Helper text displayed below the input. */
    hint?: ReactNode;
    /** Whether to hide required indicator from label. */
    hideRequiredIndicator?: boolean;
    /** Ref for the wrapping group element. */
    groupRef?: Ref<HTMLDivElement>;
}

const InputNumberRoot = React.forwardRef<HTMLInputElement, InputNumberProps>(
    (
        {
            size = 'sm',
            placeholder,
            label,
            hint,
            hideRequiredIndicator,
            className,
            groupRef,
            inputClassName,
            wrapperClassName,
            orientation,
            canDecrementToEmpty,
            ...props
        },
        ref,
    ) => {
        return (
            <Field
                as={NumberField}
                aria-label={!label ? placeholder : undefined}
                {...props}
                className={className}
                orientation={orientation}
                canDecrementToEmpty={canDecrementToEmpty}
            >
                {({ isDisabled, isInvalid, isRequired }) => (
                    <>
                        {label && (
                            <Field.Label
                                size={size}
                                isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired}
                            >
                                {label}
                            </Field.Label>
                        )}

                        <InputNumberBase
                            {...{
                                ref,
                                groupRef,
                                size,
                                placeholder,
                                inputClassName,
                                wrapperClassName,
                                isDisabled,
                                isInvalid,
                            }}
                        />

                        {hint && <Field.HintText isInvalid={isInvalid}>{hint}</Field.HintText>}
                    </>
                )}
            </Field>
        );
    },
);

InputNumberRoot.displayName = SEMANTIC_NAME;

export const InputNumber = Object.assign(InputNumberRoot, {
    Base: InputNumberBase,
});
